<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useSalesGoalsStore } from 'dashboard/stores/salesGoals';
import { useAlert } from 'dashboard/composables';

const emit = defineEmits(['saved']);

const { t } = useI18n();
const agents = useMapGetter('agents/getAgents');
const goalsStore = useSalesGoalsStore();

const isOpen = ref(false);
const isLoading = ref(false);
const isSaving = ref(false);
const isStandaloneMode = ref(false); // true when opened without a specific agent
const agentId = ref(null);
const agentName = ref('');
const selectedYear = ref(new Date().getFullYear());
const selectedMonth = ref(null);
const goals = ref([]);

const editingForm = reactive({
  goalType: 'value',
  goalValue: '',
});

const MONTH_NAMES_PT = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

const monthNames = computed(() => {
  try {
    const keys = t('SALES.GOAL_CALENDAR.MONTHS');
    return Array.isArray(keys) ? keys : MONTH_NAMES_PT;
  } catch {
    return MONTH_NAMES_PT;
  }
});

const agentOptions = computed(() =>
  (agents.value || []).map(a => ({ value: a.id, label: a.name }))
);

const goalByMonth = computed(() =>
  Object.fromEntries(
    goals.value.map(g => {
      const parts = String(g.start_date).slice(0, 10).split('-');
      const month = parseInt(parts[1], 10) - 1;
      return [month, g];
    })
  )
);

const selectedGoal = computed(() =>
  selectedMonth.value !== null ? goalByMonth.value[selectedMonth.value] : null
);

const modalTitle = computed(() => {
  if (isStandaloneMode.value) return t('SALES.GOAL_CALENDAR.TITLE_GENERIC');
  return agentName.value
    ? t('SALES.GOAL_CALENDAR.TITLE', { name: agentName.value })
    : t('SALES.GOAL_CALENDAR.TITLE_GENERIC');
});

const brl = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatGoalValue = goal => {
  if (!goal) return null;
  if (goal.goal_type === 'value') return brl(goal.goal_value);
  return `${Number(goal.goal_value)}`;
};

async function fetchGoals() {
  if (!agentId.value) return;
  isLoading.value = true;
  goals.value = [];
  try {
    goals.value = await goalsStore.fetchGoalsByAgent(agentId.value, selectedYear.value);
  } catch {
    goals.value = [];
  } finally {
    isLoading.value = false;
  }
}

async function onAgentSelected(val) {
  if (!val) return;
  agentId.value = val;
  const found = (agents.value || []).find(a => a.id === val);
  agentName.value = found?.name ?? '';
  selectedMonth.value = null;
  editingForm.goalType = 'value';
  editingForm.goalValue = '';
  await fetchGoals();
}

function selectMonth(monthIdx) {
  if (selectedMonth.value === monthIdx) {
    selectedMonth.value = null;
    return;
  }
  selectedMonth.value = monthIdx;
  const existing = goalByMonth.value[monthIdx];
  editingForm.goalType = existing?.goal_type ?? 'value';
  editingForm.goalValue = existing?.goal_value ? String(Number(existing.goal_value)) : '';
}

async function handleSave() {
  if (!editingForm.goalValue || Number(editingForm.goalValue) <= 0) return;
  isSaving.value = true;

  const mm = String(selectedMonth.value + 1).padStart(2, '0');
  const startDate = `${selectedYear.value}-${mm}-01`;

  const payload = {
    agent_id: agentId.value,
    goal_type: editingForm.goalType,
    goal_value: Number(editingForm.goalValue),
    period_type: 'monthly',
    start_date: startDate,
  };

  try {
    const existing = selectedGoal.value;
    if (existing) {
      await goalsStore.updateGoal(existing.id, payload);
    } else {
      await goalsStore.createGoal(payload);
    }
    useAlert(t('SALES.GOAL_CALENDAR.SAVE_SUCCESS'));
    emit('saved');
    selectedMonth.value = null;
    await fetchGoals();
  } catch {
    useAlert(t('SALES.GOAL_CALENDAR.ERROR'), { variant: 'error' });
  } finally {
    isSaving.value = false;
  }
}

async function handleRemove() {
  if (!selectedGoal.value) return;
  if (!window.confirm(t('SALES.GOAL_CALENDAR.CONFIRM_REMOVE'))) return;
  isSaving.value = true;
  try {
    await goalsStore.deleteGoal(selectedGoal.value.id);
    useAlert(t('SALES.GOAL_CALENDAR.REMOVE_SUCCESS'));
    emit('saved');
    selectedMonth.value = null;
    await fetchGoals();
  } catch {
    useAlert(t('SALES.GOAL_CALENDAR.ERROR'), { variant: 'error' });
  } finally {
    isSaving.value = false;
  }
}

async function changeYear(delta) {
  selectedYear.value += delta;
  selectedMonth.value = null;
  await fetchGoals();
}

async function open(agId = null, agName = '') {
  isStandaloneMode.value = !agId;
  agentId.value = agId;
  agentName.value = agName || '';
  selectedYear.value = new Date().getFullYear();
  selectedMonth.value = null;
  goals.value = [];
  editingForm.goalType = 'value';
  editingForm.goalValue = '';
  isOpen.value = true;
  if (agId) await fetchGoals();
}

function close() {
  isOpen.value = false;
}

defineExpose({ open, close });
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="close" />

      <div
        class="relative z-10 w-full max-w-lg flex flex-col rounded-2xl bg-n-surface-1 border border-n-weak shadow-xl overflow-hidden"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-n-weak shrink-0">
          <div>
            <h2 class="text-base font-semibold text-n-slate-12">{{ modalTitle }}</h2>
            <p v-if="isStandaloneMode && agentName" class="text-xs text-n-slate-9 mt-0.5">
              {{ agentName }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <div class="flex items-center gap-1 rounded-lg border border-n-strong px-1 py-0.5">
              <button
                class="p-1 rounded text-n-slate-9 hover:text-n-slate-12 transition-colors"
                @click="changeYear(-1)"
              >
                <span class="i-lucide-chevron-left text-sm" />
              </button>
              <span class="text-sm font-medium text-n-slate-12 min-w-[2.5rem] text-center">
                {{ selectedYear }}
              </span>
              <button
                class="p-1 rounded text-n-slate-9 hover:text-n-slate-12 transition-colors"
                @click="changeYear(1)"
              >
                <span class="i-lucide-chevron-right text-sm" />
              </button>
            </div>
            <button
              class="p-1.5 rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="close"
            >
              <span class="i-lucide-x text-lg" />
            </button>
          </div>
        </div>

        <!-- Agent selector — always visible in standalone mode so admin can switch agents -->
        <div v-if="isStandaloneMode" class="px-5 pt-4 pb-0">
          <label class="text-xs font-medium text-n-slate-11 mb-1 block">Agente</label>
          <select
            class="w-full rounded-lg border border-n-strong bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            @change="e => onAgentSelected(Number(e.target.value) || null)"
          >
            <option value="">Selecione um agente para ver as metas</option>
            <option
              v-for="agent in agentOptions"
              :key="agent.value"
              :value="agent.value"
              :selected="agent.value === agentId"
            >
              {{ agent.label }}
            </option>
          </select>
        </div>

        <!-- Month grid -->
        <div class="px-5 py-4">
          <div v-if="isLoading" class="flex justify-center py-8">
            <span class="i-lucide-loader-circle animate-spin text-2xl text-n-slate-9" />
          </div>

          <div v-else-if="agentId" class="grid grid-cols-4 gap-2">
            <button
              v-for="(name, idx) in monthNames"
              :key="idx"
              class="relative flex flex-col items-center rounded-xl border py-3 px-2 gap-1.5 transition-all text-center focus:outline-none"
              :class="
                selectedMonth === idx
                  ? 'border-n-brand bg-n-brand/10'
                  : 'border-n-strong bg-n-alpha-3 hover:border-n-brand/50 hover:bg-n-alpha-4'
              "
              @click="selectMonth(idx)"
            >
              <span class="text-xs font-medium text-n-slate-11">{{ name }}</span>

              <template v-if="goalByMonth[idx]">
                <span class="text-xs font-bold text-n-slate-12 leading-tight break-all">
                  {{ formatGoalValue(goalByMonth[idx]) }}
                </span>
              </template>
              <template v-else>
                <span class="text-xs text-n-slate-8">—</span>
              </template>
            </button>
          </div>

          <!-- No agent selected yet (standalone mode) -->
          <div v-else class="flex flex-col items-center gap-2 py-8 text-n-slate-9">
            <span class="i-lucide-target text-3xl" />
            <p class="text-sm text-center">Selecione um agente acima para<br>visualizar e editar as metas</p>
          </div>
        </div>

        <!-- Inline form (when month is selected) -->
        <div
          v-if="selectedMonth !== null && agentId"
          class="border-t border-n-weak px-5 py-4 flex flex-col gap-3"
        >
          <p class="text-sm font-semibold text-n-slate-12">
            {{
              $t('SALES.GOAL_CALENDAR.GOAL_FOR_MONTH', {
                month: monthNames[selectedMonth],
                year: selectedYear,
              })
            }}
          </p>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('SALES.GOAL_FORM.GOAL_TYPE_LABEL') }}
            </label>
            <select
              v-model="editingForm.goalType"
              class="w-full rounded-lg border border-n-strong bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            >
              <option value="value">{{ $t('SALES.GOAL_CALENDAR.GOAL_TYPE_VALUE') }}</option>
              <option value="quantity">{{ $t('SALES.GOAL_CALENDAR.GOAL_TYPE_QUANTITY') }}</option>
            </select>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ $t('SALES.GOAL_FORM.GOAL_VALUE_LABEL') }}
            </label>
            <input
              v-model="editingForm.goalValue"
              type="number"
              min="0"
              step="0.01"
              :placeholder="
                editingForm.goalType === 'value'
                  ? $t('SALES.GOAL_CALENDAR.GOAL_VALUE_PLACEHOLDER_VALUE')
                  : $t('SALES.GOAL_CALENDAR.GOAL_VALUE_PLACEHOLDER_QTY')
              "
              class="w-full rounded-lg border border-n-strong bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
          </div>

          <div class="flex items-center justify-between gap-2 pt-1">
            <button
              v-if="selectedGoal"
              class="text-xs text-s-red-500 hover:text-s-red-400 flex items-center gap-1 transition-colors"
              :disabled="isSaving"
              @click="handleRemove"
            >
              <span class="i-lucide-trash-2 text-sm" />
              {{ $t('SALES.GOAL_CALENDAR.REMOVE_GOAL') }}
            </button>
            <div v-else />

            <div class="flex items-center gap-2">
              <button
                class="px-3 py-1.5 text-xs rounded-lg text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                @click="selectedMonth = null"
              >
                {{ $t('SALES.GOAL_CALENDAR.CANCEL') }}
              </button>
              <button
                class="px-4 py-1.5 text-xs rounded-lg bg-n-brand text-white hover:bg-n-brand-dark transition-colors disabled:opacity-50"
                :disabled="isSaving || !editingForm.goalValue || Number(editingForm.goalValue) <= 0"
                @click="handleSave"
              >
                <span v-if="isSaving" class="i-lucide-loader-circle animate-spin mr-1 text-sm" />
                {{ $t('SALES.GOAL_CALENDAR.SAVE') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
